import { TrashIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { getFileNameUrl } from "configs/images.config";
import { opportunityData } from "configs/opportunity.config";
import { format } from "date-fns";
import { assetKeyToTitleMapper } from "pages/application/opportunities/edit/loanDetails/elements/overview/subAssetType/constants";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { OpportunityController } from "services/opportunity/controller";
import { Card, Dropdown, Label } from "stories";
import { useToast } from "utils/customHooks/useToast";
import classes from "./OpportunityCard.module.scss";

const LoadingOpportunityCard = () => {
  return (
    <Card>
      <div className={`d-flex ${classes["wrapper"]}`}>
        <div className={classes["image"]}>
          <Skeleton
            height={"100%"}
            borderRadius={0}
            style={{ lineHeight: "unset" }}
          />
        </div>
        <div className={classes["body"]}>
          <div className="d-flex justify-content-between">
            <div>
              <div className={classes["font-title"]}>
                <Skeleton width={"120px"} />
              </div>
              <div className={classes["font-amount"]}>
                <Skeleton width={"220px"} />
              </div>
            </div>
            <div
              className={`d-flex align-items-center ${classes["option-wrapper"]}`}
            >
              <Skeleton width={"60px"} className={"me-2"} />
              <Skeleton width={"60px"} />
              <div className={"font-gray fw-bold font-14"}>
                <Skeleton width={"60px"} />
              </div>
              <div className={classes["dropdown-wrapper"]}>
                <Skeleton height={"45px"} />
              </div>
            </div>
          </div>
          <div className="d-flex">
            {new Array(6).map((item) => (
              <div className={classes["detail"]} key={item.title}>
                <Skeleton width={"70px"} />
                <Skeleton width={"90px"} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

const { updateVisibility } = OpportunityController();
const OpportunityCard = ({ readOnly, loading, data, onDelete, isDeleting }) => {
  const [opportunityImage, setOpportunityImage] = useState("");
  const assetTypeValue = Object.keys(data?.asset_classes || {})
    .filter((key) => Object.keys(data?.asset_classes[key]).length > 0)
    .map((key) => assetKeyToTitleMapper[key])
    .join(", ");
  const details = [
    {
      title: "Opportunity Name",
      value: data?.project_name,
    },
    {
      title: "Location",
      value: data?.location,
    },
    {
      title: "Date Posted",
      value: data?.create_time
        ? format(new Date(data?.create_time), "MMMM dd, yyyy")
        : "-",
    },
    {
      title: "Closing Date",
    },
    {
      title: "Loan Type",
      value: data?.loan_type,
      className: "capitalize",
    },
    {
      title: "Asset Type",
      value: assetTypeValue,
      className: "capitalize",
    },
  ];

  useEffect(() => {
    let cover = null;
    if (data?.images) {
      cover = data.images[0];
      for (let i = 0; i < data.images.length; i++) {
        if (data.images[i].cover) {
          cover = data.images[i];
        }
      }
    }
    if (cover && cover.filename) {
      setOpportunityImage(cover.filename);
    } else {
      setOpportunityImage(null);
    }
  }, [data]); //eslint-disable-line
  const { showToast } = useToast();
  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) onDelete();
  };
  const handleVisibilityChange = (e) => {
    updateVisibility(data.id, { visibility: e.target.value }).then(() => {
      showToast(
        "The visibility of the opportunity updated successfully",
        "success"
      );
    });
  };
  if (loading && (!data || Object.keys(data).length < 3)) {
    return <LoadingOpportunityCard />;
  }
  return (
    <div className={"mb-4"}>
      <Link to={`/opportunities/${data.id}`}>
        <Card>
          <div
            className={classNames("d-flex", classes["wrapper"], {
              [classes["action"]]: !readOnly,
            })}
          >
            <div className={classes["image"]}>
              {opportunityImage && (
                <img src={getFileNameUrl(opportunityImage)} alt="Opportunity" />
              )}
            </div>
            <div className={classes["body"]}>
              <div className="d-flex justify-content-between">
                <div>
                  <div className={classes["font-title"]}>Amount Requested</div>
                  <div className={classes["font-amount"]}>
                    ${data?.amount_requested?.toLocaleString()}
                  </div>
                </div>
                <div
                  className={`d-flex align-items-center ${classes["option-wrapper"]}`}
                >
                  {!readOnly && (
                    <>
                      <Label text={"Pending Approval"} />
                      <Label text={"Saved"} />
                    </>
                  )}
                  <div className={"font-gray fw-bold font-14"}>
                    ID: {data?.id}
                  </div>
                  {!readOnly && (
                    <div
                      className={classes["dropdown-wrapper"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Dropdown
                        ignoreOffsetTop
                        list={opportunityData.projectVisibility}
                        fullWidth
                        containerClassName="mb-0"
                        placeholder={"Select"}
                        onChange={handleVisibilityChange}
                        defaultValue={data.visibility}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex">
                {details.map((item) => (
                  <div
                    className={classNames(classes["detail"], "max-w-1/6")}
                    key={item.title}
                  >
                    <div className={classes["font-title"]}>{item.title}</div>
                    <div
                      title={item.value}
                      className={classNames(
                        classes["font-detail"],
                        "truncate",
                        item.className
                      )}
                    >
                      {item.value || "-"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!readOnly && (
              <button
                className={classes["deleteSection"]}
                onClick={handleDelete}
              >
                {!isDeleting ? (
                  <TrashIcon />
                ) : (
                  <span className={classes["loading"]}></span>
                )}
                <span>Delete</span>
              </button>
            )}
          </div>
        </Card>
      </Link>
    </div>
  );
};

export default OpportunityCard;
